import { cva } from "class-variance-authority";
import { InputHTMLAttributes, LabelHTMLAttributes } from "react";

import { cn } from "@/lib/tailwind";

export interface ICheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label?: React.ReactNode;
  labelProps?: Omit<LabelHTMLAttributes<HTMLLabelElement>, "children">;
}

const checkboxCVA = cva(
  "all ease size-5 cursor-pointer appearance-none border-2 border-black transition-colors checked:border-none checked:bg-black checked:outline-none checked:ring-black hover:bg-transparent hover:checked:bg-black focus:ring-0 focus:checked:bg-black",
  {
    variants: {
      disabled: {
        false: "",
        true: "disabled:cursor-not-allowed disabled:opacity-50",
      },
    },
  },
);

const CheckboxContainer = ({
  disabled,
  className,
  ...restProps
}: Omit<ICheckboxProps, "label" | "labelProps">) => {
  const classes = cn(checkboxCVA({ disabled }), className);
  return <input type="checkbox" className={classes} disabled={disabled} {...restProps} />;
};

const Checkbox = ({ label, labelProps = {}, ...restProps }: ICheckboxProps) => {
  if (!label) return <CheckboxContainer {...restProps} />;

  const disabled = restProps.disabled;
  const { className, ...restLabelProps } = labelProps;

  return (
    <fieldset disabled={disabled} className={cn(disabled && "cursor-not-allowed opacity-50")}>
      <label
        className={cn(
          "flex cursor-pointer items-center gap-[0.625rem] text-[0.813rem] font-normal leading-4 text-black",
          disabled && "cursor-not-allowed",
          className,
        )}
        {...restLabelProps}
      >
        <CheckboxContainer {...restProps} />
        <span className="mb-[-0.094rem]">{label}</span>
      </label>
    </fieldset>
  );
};

export default Checkbox;
